import { useState, useMemo } from "react";
import type { ReactElement } from "react";

import clsx from "clsx";
import { deburr } from "../deburr";
import { TextField } from "./TextField";
import {
  Pin,
  Trash2,
  PackageMinus,
  PackagePlus,
  Archive,
  FileText,
  FolderPlus,
  Edit2,
  ChevronDown,
  ChevronRight,
  Folder,
  Folders,
} from "lucide-react";

import "./WhiteboardsMenu.scss";

export interface WhiteboardFolder {
  id: string;
  profileId: string;
  name: string;
  parentId: string | null;
  createdAt: number;
}

interface WhiteboardsMenuProps {
  whiteboards: Array<{
    id: string;
    name: string;
    lastOpened: number;
    thumbnailUrl?: string;
    folderId?: string | null;
    pinned?: boolean;
  }>;
  folders?: WhiteboardFolder[];
  currentWhiteboard: { id: string; name: string } | null;
  onSelectWhiteboard: (id: string) => void;
  onCreateWhiteboard: (name: string) => void;
  onDeleteWhiteboard: (id: string) => void;
  onRenameWhiteboard: (id: string, name: string) => void;
  onCreateFolder?: (name: string, parentId: string | null) => void;
  onRenameFolder?: (folderId: string, name: string) => void;
  onDeleteFolder?: (folderId: string) => void;
  onMoveWhiteboard?: (whiteboardId: string, folderId: string | null) => void;
  onPinWhiteboard?: (whiteboardId: string, pinned: boolean) => void;
}

export const WhiteboardsMenu = ({
  whiteboards,
  folders = [],
  currentWhiteboard,
  onSelectWhiteboard,
  onCreateWhiteboard,
  onDeleteWhiteboard,
  onRenameWhiteboard,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onMoveWhiteboard,
  onPinWhiteboard,
}: WhiteboardsMenuProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newWhiteboardName, setNewWhiteboardName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"flat" | "folders" | "recent">(
    "flat", // Changed from "recent" - can be changed back to enable recent view
  );
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [draggedWhiteboard, setDraggedWhiteboard] = useState<string | null>(
    null,
  );
  const [folderPickerOpen, setFolderPickerOpen] = useState<string | null>(null);
  const [expandedThumbnail, setExpandedThumbnail] = useState<{
    url: string;
    name: string;
  } | null>(null);

  const handleCreate = () => {
    if (!newWhiteboardName.trim()) {
      return;
    }
    onCreateWhiteboard(newWhiteboardName.trim());
    setNewWhiteboardName("");
    setIsCreating(false);
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this whiteboard?")) {
      onDeleteWhiteboard(id);
    }
  };

  const handleStartRename = (
    id: string,
    currentName: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setRenamingId(id);
    setRenameValue(currentName);
  };

  const handleRename = (id: string) => {
    if (!renameValue.trim()) {
      return;
    }
    onRenameWhiteboard(id, renameValue.trim());
    setRenamingId(null);
    setRenameValue("");
  };

  const handleCancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const filteredWhiteboards = useMemo(() => {
    const search = deburr(searchQuery.trim().toLowerCase());
    let filtered = whiteboards;
    if (search) {
      filtered = whiteboards.filter((wb) => {
        const name = wb.name || "";
        return name.trim() && deburr(name.toLowerCase()).includes(search);
      });
    }
    // In flat view, sort by pinned status first, then by lastOpened
    if (viewMode === "flat") {
      return [...filtered].sort((a, b) => {
        // Pinned items first
        if (a.pinned !== b.pinned) {
          return a.pinned ? -1 : 1;
        }
        // Then sort by lastOpened
        return b.lastOpened - a.lastOpened;
      });
    }
    return filtered;
  }, [whiteboards, searchQuery, viewMode]);

  const recentWhiteboards = useMemo(() => {
    return [...whiteboards]
      .sort((a, b) => b.lastOpened - a.lastOpened)
      .slice(0, 10);
  }, [whiteboards]);

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleDragStart = (whiteboardId: string) => {
    setDraggedWhiteboard(whiteboardId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDropOnFolder = (folderId: string | null) => {
    if (draggedWhiteboard && onMoveWhiteboard) {
      onMoveWhiteboard(draggedWhiteboard, folderId);
      setDraggedWhiteboard(null);
    }
  };

  const handleMoveToFolder = (
    whiteboardId: string,
    folderId: string | null,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (onMoveWhiteboard) {
      onMoveWhiteboard(whiteboardId, folderId);
      setFolderPickerOpen(null);
    }
  };

  const renderFolderOptions = (
    whiteboardId: string,
    currentFolderId: string | null,
  ): ReactElement => {
    const renderFolderList = (
      parentId: string | null,
      level = 0,
    ): ReactElement[] => {
      return folders
        .filter((f) => f.parentId === parentId)
        .map((folder) => (
          <div key={folder.id}>
            <button
              className="whiteboards-menu__folder-option"
              style={{ paddingLeft: `${level * 16 + 8}px` }}
              onClick={(e) => handleMoveToFolder(whiteboardId, folder.id, e)}
            >
              📁 {folder.name}
            </button>
            {renderFolderList(folder.id, level + 1)}
          </div>
        ));
    };

    return (
      <div
        className="whiteboards-menu__folder-picker"
        onClick={(e) => e.stopPropagation()}
      >
        {currentFolderId && (
          <button
            className="whiteboards-menu__folder-option whiteboards-menu__folder-option--remove"
            onClick={(e) => handleMoveToFolder(whiteboardId, null, e)}
          >
            ↩️ Remove from folder
          </button>
        )}
        <button
          className="whiteboards-menu__folder-option"
          onClick={(e) => handleMoveToFolder(whiteboardId, null, e)}
        >
          📄 Root (No folder)
        </button>
        {renderFolderList(null)}
      </div>
    );
  };

  const renderWhiteboardItem = (
    wb: WhiteboardsMenuProps["whiteboards"][0],
    showThumbnail = true,
  ) => (
    <div className="whiteboards-menu__item-body">
      {showThumbnail && (
        <>
          {/* Left: Thumbnail (spans both rows) */}
          <div className="whiteboards-menu__thumbnail-container">
            {wb.thumbnailUrl ? (
              <img
                src={wb.thumbnailUrl}
                alt={wb.name}
                className="whiteboards-menu__thumbnail"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedThumbnail({
                    url: wb.thumbnailUrl!,
                    name: wb.name,
                  });
                }}
              />
            ) : (
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <rect
                  width="100"
                  height="100"
                  fill="var(--color-gray-10)"
                  stroke="var(--color-gray-30)"
                  strokeWidth="2"
                />
                <path
                  d="M20,30 L80,30 M20,50 L80,50 M20,70 L50,70"
                  stroke="var(--color-gray-50)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
          {/* Top right: Date */}
          <div className="whiteboards-menu__item-date-cell">
            <span className="whiteboards-menu__item-date">
              {new Date(wb.lastOpened).toLocaleDateString()}
            </span>
          </div>
          {/* Top right: Actions (same row as date) */}
          <div className="whiteboards-menu__item-actions">
            {viewMode === "flat" && onPinWhiteboard && (
              <button
                className={clsx("whiteboards-menu__item-pin", {
                  "whiteboards-menu__item-pin--pinned": wb.pinned,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  onPinWhiteboard(wb.id, !wb.pinned);
                }}
                title={wb.pinned ? "Unpin whiteboard" : "Pin whiteboard"}
                aria-label={wb.pinned ? "Unpin whiteboard" : "Pin whiteboard"}
              >
                <Pin size={18} />
              </button>
            )}
            {onMoveWhiteboard && (
              <div className="whiteboards-menu__folder-move-container">
                <button
                  className="whiteboards-menu__item-folder"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderPickerOpen(
                      folderPickerOpen === wb.id ? null : wb.id,
                    );
                  }}
                  title={
                    wb.folderId ? "Move to different folder" : "Move to folder"
                  }
                  aria-label="Move to folder"
                >
                  {wb.folderId ? (
                    <PackageMinus size={18} />
                  ) : (
                    <PackagePlus size={18} />
                  )}
                </button>
                {folderPickerOpen === wb.id &&
                  renderFolderOptions(wb.id, wb.folderId || null)}
              </div>
            )}
            <button
              className="whiteboards-menu__item-delete"
              onClick={(e) => handleDelete(wb.id, e)}
              title="Delete whiteboard"
              aria-label="Delete whiteboard"
            >
              <Trash2 size={18} />
            </button>
          </div>
          {/* Bottom: Title (spans across) */}
          <div className="whiteboards-menu__item-info">
            {renamingId === wb.id ? (
              <input
                type="text"
                className="whiteboards-menu__rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    handleRename(wb.id);
                  }
                  if (e.key === "Escape") {
                    handleCancelRename();
                  }
                }}
                onBlur={() => handleRename(wb.id)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                className="whiteboards-menu__item-name"
                onDoubleClick={(e) => handleStartRename(wb.id, wb.name, e)}
                title={wb.name}
              >
                {wb.name}
              </span>
            )}
          </div>
        </>
      )}
      {!showThumbnail && (
        <div className="whiteboards-menu__item-details">
          <div className="whiteboards-menu__item-actions">
            {viewMode === "flat" && onPinWhiteboard && (
              <button
                className={clsx("whiteboards-menu__item-pin", {
                  "whiteboards-menu__item-pin--pinned": wb.pinned,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  onPinWhiteboard(wb.id, !wb.pinned);
                }}
                title={wb.pinned ? "Unpin whiteboard" : "Pin whiteboard"}
                aria-label={wb.pinned ? "Unpin whiteboard" : "Pin whiteboard"}
              >
                <Pin size={18} />
              </button>
            )}
            {onMoveWhiteboard && (
              <div className="whiteboards-menu__folder-move-container">
                <button
                  className="whiteboards-menu__item-folder"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderPickerOpen(
                      folderPickerOpen === wb.id ? null : wb.id,
                    );
                  }}
                  title={
                    wb.folderId ? "Move to different folder" : "Move to folder"
                  }
                  aria-label="Move to folder"
                >
                  {wb.folderId ? (
                    <PackageMinus size={18} />
                  ) : (
                    <PackagePlus size={18} />
                  )}
                </button>
                {folderPickerOpen === wb.id &&
                  renderFolderOptions(wb.id, wb.folderId || null)}
              </div>
            )}
            <button
              className="whiteboards-menu__item-delete"
              onClick={(e) => handleDelete(wb.id, e)}
              title="Delete whiteboard"
              aria-label="Delete whiteboard"
            >
              <Trash2 size={18} />
            </button>
          </div>
          <div className="whiteboards-menu__item-info">
            {renamingId === wb.id ? (
              <input
                type="text"
                className="whiteboards-menu__rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    handleRename(wb.id);
                  }
                  if (e.key === "Escape") {
                    handleCancelRename();
                  }
                }}
                onBlur={() => handleRename(wb.id)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <>
                <span
                  className="whiteboards-menu__item-name"
                  onDoubleClick={(e) => handleStartRename(wb.id, wb.name, e)}
                  title={wb.name}
                >
                  {wb.name}
                </span>
                <span className="whiteboards-menu__item-date">
                  {new Date(wb.lastOpened).toLocaleDateString()}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderFolderTree = (
    parentId: string | null,
    level = 0,
  ): ReactElement[] => {
    const childFolders = folders.filter((f) => f.parentId === parentId);
    const folderWhiteboards = filteredWhiteboards.filter(
      (wb) => (wb.folderId ?? null) === parentId,
    );

    const elements: ReactElement[] = [];

    childFolders.forEach((folder) => {
      const isExpanded = expandedFolders.has(folder.id);
      elements.push(
        <div key={folder.id} style={{ marginLeft: `${level * 16}px` }}>
          <div
            className="whiteboards-menu__folder"
            onDragOver={handleDragOver}
            onDrop={(e) => {
              e.stopPropagation();
              handleDropOnFolder(folder.id);
            }}
            onClick={() => toggleFolder(folder.id)}
          >
            <div className="whiteboards-menu__folder-left">
              <button
                className="whiteboards-menu__folder-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(folder.id);
                }}
              >
                {isExpanded ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
              <span
                className="whiteboards-menu__folder-name"
                title={folder.name}
              >
                <Folder size={18} /> {folder.name}
              </span>
            </div>
            <div className="whiteboards-menu__folder-actions">
              {onCreateFolder && (
                <button
                  className="whiteboards-menu__folder-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    const name = prompt("Folder name:");
                    if (name?.trim()) {
                      onCreateFolder(name.trim(), folder.id);
                      setExpandedFolders(
                        new Set(expandedFolders).add(folder.id),
                      );
                    }
                  }}
                  title="Create subfolder"
                >
                  <FolderPlus size={18} />
                </button>
              )}
              {onRenameFolder && (
                <button
                  className="whiteboards-menu__folder-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    const name = prompt("New folder name:", folder.name);
                    if (name?.trim()) {
                      onRenameFolder(folder.id, name.trim());
                    }
                  }}
                  title="Rename folder"
                >
                  <Edit2 size={18} />
                </button>
              )}
              {onDeleteFolder && (
                <button
                  className="whiteboards-menu__folder-action whiteboards-menu__folder-action--delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete folder "${folder.name}"?`)) {
                      onDeleteFolder(folder.id);
                    }
                  }}
                  title="Delete folder"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          </div>
          {isExpanded && renderFolderTree(folder.id, level + 1)}
        </div>,
      );
    });

    folderWhiteboards.forEach((wb) => {
      elements.push(
        <div
          key={wb.id}
          className={clsx(
            "whiteboards-menu__item",
            "whiteboards-menu__item--compact",
            {
              "whiteboards-menu__item--active": currentWhiteboard?.id === wb.id,
            },
          )}
          style={{ marginLeft: `${level * 16}px` }}
          onClick={() => renamingId !== wb.id && onSelectWhiteboard(wb.id)}
          draggable
          onDragStart={() => handleDragStart(wb.id)}
        >
          <div className="whiteboards-menu__item-content">
            {renderWhiteboardItem(wb, false)}
          </div>
        </div>,
      );
    });

    return elements;
  };

  return (
    <div className="whiteboards-menu">
      <div className="whiteboards-menu__header">
        <h3>Saved Whiteboards</h3>
        <div className="whiteboards-menu__controls">
          <TextField
            type="search"
            className="whiteboards-menu__search"
            placeholder="Search whiteboards..."
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
          />
          {onCreateFolder && (
            <button
              className="whiteboards-menu__view-toggle"
              onClick={() =>
                setViewMode((prev) => {
                  // Toggle between flat and folders only (recent view hidden but functional)
                  // To re-enable recent view: add back "recent" to the cycle
                  if (prev === "flat") return "folders";
                  return "flat";
                })
              }
              title={
                viewMode === "flat" ? "Show folders" : "Show all whiteboards"
              }
            >
              {viewMode === "flat" ? (
                <Folders size={20} />
              ) : (
                <FileText size={20} />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="whiteboards-menu__list">
        {viewMode === "recent" ? (
          recentWhiteboards.length === 0 ? (
            <div className="whiteboards-menu__empty">No saved whiteboards</div>
          ) : (
            <>
              <div className="whiteboards-menu__section-header">
                Recently Used (Top 10)
              </div>
              {recentWhiteboards.map((wb) => (
                <div
                  key={wb.id}
                  className={clsx("whiteboards-menu__item", {
                    "whiteboards-menu__item--active":
                      currentWhiteboard?.id === wb.id,
                  })}
                  onClick={() => onSelectWhiteboard(wb.id)}
                >
                  <div className="whiteboards-menu__item-content">
                    {wb.thumbnailUrl && (
                      <div className="whiteboards-menu__thumbnail-container">
                        <img
                          src={wb.thumbnailUrl}
                          alt={wb.name}
                          className="whiteboards-menu__thumbnail"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedThumbnail({
                              url: wb.thumbnailUrl!,
                              name: wb.name,
                            });
                          }}
                        />
                      </div>
                    )}
                    <div className="whiteboards-menu__item-info">
                      <span className="whiteboards-menu__item-name">
                        {wb.name}
                      </span>
                      <span className="whiteboards-menu__item-date">
                        {new Date(wb.lastOpened).toLocaleDateString()}
                      </span>
                      <span className="whiteboards-menu__item-date">
                        {new Date(wb.lastOpened).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                  <div className="whiteboards-menu__item-actions">
                    <button
                      className="whiteboards-menu__item-delete"
                      onClick={(e) => handleDelete(wb.id, e)}
                      title="Delete whiteboard"
                      aria-label="Delete whiteboard"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </>
          )
        ) : viewMode === "folders" ? (
          <>
            {onCreateFolder && (
              <button
                className="whiteboards-menu__new-folder-btn"
                onClick={() => {
                  const name = prompt("Folder name:");
                  if (name?.trim()) {
                    onCreateFolder(name.trim(), null);
                  }
                }}
              >
                ➕ New Folder
              </button>
            )}
            <div
              className="whiteboards-menu__folder-root"
              onDragOver={handleDragOver}
              onDrop={(e) => {
                e.preventDefault();
                handleDropOnFolder(null);
              }}
            >
              {renderFolderTree(null)}
            </div>
          </>
        ) : filteredWhiteboards.length === 0 ? (
          <div className="whiteboards-menu__empty">No saved whiteboards</div>
        ) : (
          filteredWhiteboards.map((wb) => (
            <div
              key={wb.id}
              className={clsx("whiteboards-menu__item", {
                "whiteboards-menu__item--active":
                  currentWhiteboard?.id === wb.id,
              })}
              onClick={() => renamingId !== wb.id && onSelectWhiteboard(wb.id)}
              draggable
              onDragStart={() => handleDragStart(wb.id)}
            >
              <div className="whiteboards-menu__item-content">
                {renderWhiteboardItem(wb)}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="whiteboards-menu__footer">
        {isCreating ? (
          <div className="whiteboards-menu__create-form">
            <input
              type="text"
              value={newWhiteboardName}
              onChange={(e) => setNewWhiteboardName(e.target.value)}
              placeholder="Whiteboard name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleCreate();
                }
                if (e.key === "Escape") {
                  setIsCreating(false);
                  setNewWhiteboardName("");
                }
              }}
            />
            <div className="whiteboards-menu__create-actions">
              <button
                className="whiteboards-menu__button whiteboards-menu__button--secondary"
                onClick={() => {
                  setIsCreating(false);
                  setNewWhiteboardName("");
                }}
              >
                Cancel
              </button>
              <button
                className="whiteboards-menu__button whiteboards-menu__button--primary"
                onClick={handleCreate}
              >
                Create
              </button>
            </div>
          </div>
        ) : (
          <button
            className="whiteboards-menu__new-btn"
            onClick={() => setIsCreating(true)}
          >
            + New Whiteboard
          </button>
        )}
      </div>

      {folderPickerOpen && (
        <div
          className="whiteboards-menu__folder-picker-backdrop"
          onClick={() => setFolderPickerOpen(null)}
        />
      )}

      {expandedThumbnail && (
        <div
          className="whiteboards-menu__thumbnail-modal"
          onClick={() => setExpandedThumbnail(null)}
        >
          <div className="whiteboards-menu__thumbnail-modal-content">
            <img
              src={expandedThumbnail.url}
              alt={expandedThumbnail.name}
              className="whiteboards-menu__thumbnail-modal-image"
            />
            <div className="whiteboards-menu__thumbnail-modal-title">
              {expandedThumbnail.name}
            </div>
            <button
              className="whiteboards-menu__thumbnail-modal-close"
              onClick={() => setExpandedThumbnail(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
